import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAddBusinessPlan } from '@editor/global/growthbookUtils';
import { getOnClickBusinessLink } from '@editor/global/newPricingPageConfig';
import analytics from '@repo/analytics';
import { TeamRole } from '@repo/domain/team';
import teamsApi from '@repo/editor/src/api/teams';
import DecisionModal from '@repo/editor/src/components/DecisionModal/DecisionModal';
import MemberSelect from '@repo/editor/src/components/PricingPlan/MemberSelect/MemberSelect';
import NewPricingPlan from '@repo/editor/src/components/PricingPlan/NewPricingPlan';
import {
  SubscriptionModal,
  SubscriptionOffboardingModal,
  getPlanPrice,
} from '@repo/editor/src/components/Subscription';
import { usePricingConfig } from '@repo/editor/src/components/Subscription/SubscriptionModal/hooks/usePricingConfig';
import { getAddonFromSubscription } from '@repo/editor/src/components/Subscription/utils';
import { ENABLE_BILLING } from '@repo/editor/src/global/constants';
import { getAddons } from '@repo/editor/src/global/pricingConfig';
import useFreeTrial from '@repo/editor/src/hooks/useFreeTrial';
import {
  usePlanSelectionStore,
  planSelectionStoreSelector,
} from '@repo/editor/src/stores/planSelectionStore';
import {
  SUCCESS_UPGRADE_PAYMENT_URL_PARAM,
  SUCCESS_UPGRADE_PAYMENT_URL_PARAM_VALUE,
  usePromotionStore,
  promotionSelector,
} from '@repo/editor/src/stores/promotionStore';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { getPriceValueInMajorUnit } from '@repo/editor/src/utils/billing';
import { withEventScope } from '@repo/event-processor';
import {
  SubscriptionPlanType,
  SubscriptionTierType,
} from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import {
  Button,
  ButtonProps,
  secondaryTheme,
} from '@repo/ui/components/Buttons';
import { Spacer } from '@repo/ui/components/Spacer';
import { fireToast } from '@repo/ui/components/Toast';
import { BREAKPOINTS } from '@repo/ui/queries';

import FreePlanMemberSelect from 'components/TeamPlans/FreePlanMemberSelect/FreePlanMemberSelect';
import {
  SUBSCRIPTION_CLICK_PLAN,
  SUBSCRIPTION_GET_PLAN,
  SUBSCRIPTION_SHOW_COMPARISON_TABLE,
} from 'global/analyticsEvents';
import { useWorkspacesFeatureEnabled } from 'global/growthbookUtils';
import useWindowSize from 'global/utils/useWindowSize';

import ComparisonTable from './ComparisonTable/ComparisonTable';
import {
  ComparisonWrapper,
  PlanContainersWrapper,
  PricingTable,
  VatNotice,
} from './styles';
import { PricingPlansProps } from './types';

const enum Modals {
  RequestUpgrade,
  Subscription,
  Offboarding,
}

const PricingPlans: React.FC<PricingPlansProps> = ({ onAuthenticate }) => {
  const [showCompare, setShowCompare] = useState(false);
  const [showModal, setShowModal] = useState<Modals | null>(null);
  const freeTrial = useFreeTrial();
  const workspaceFeatureEnabled = useWorkspacesFeatureEnabled();
  const businessPlanIsEnabled = useAddBusinessPlan();

  const showUpgradeModal = usePromotionStore(
    promotionSelector.showUpgradeModal
  );

  // if the user is logged out we save their choice to redirect after the auth
  const [subscribeAfterAuth, setSubscribeAfterAuth] =
    useState<SubscriptionPlanType | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const user = useUserStore(userStoreSelector.user);
  const isTeam = user?.isTeam ?? false;
  const isTeamAdmin = user?.team?.role === TeamRole.Admin;

  const yearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);
  const quantity = usePlanSelectionStore(planSelectionStoreSelector.quantity);
  const expertAddonPriceId = usePlanSelectionStore(
    planSelectionStoreSelector.expertAddonPriceId
  );

  const selectedExpertAddon = getAddons()?.find(
    (addon) => addon.stripeId === expertAddonPriceId
  );

  const subscriptionPrices = useSubscriptionStore(
    subscriptionStoreSelector.subscriptionPrices
  );
  const openBillingPortal = useSubscriptionStore(
    subscriptionStoreSelector.openBillingPortal
  );

  const subscriptionInitialized = useSubscriptionStore(
    subscriptionStoreSelector.isInitialized
  );
  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );
  const currentPlan = currentSubscription
    ? (currentSubscription?.plan?.name ?? SubscriptionPlanType.FREE)
    : undefined;

  const country = useSubscriptionStore(
    subscriptionStoreSelector.requestCountry
  );
  const pricingConfig = usePricingConfig(
    freeTrial.isAvailable,
    !!selectedExpertAddon?.generationCredits,
    subscriptionPrices
  );

  const { width } = useWindowSize();

  // this function should only be called when there is a user
  const handleTrackPlan = useCallback(
    (
      plan: SubscriptionPlanType | 'BUSINESS',
      event: string,
      extra?: Record<string, unknown>
    ): void => {
      const productValue =
        plan === 'BUSINESS'
          ? yearly
            ? 1740
            : 145
          : // TODO: We should ideally be using minor units here.
            getPriceValueInMajorUnit(
              getPlanPrice({
                plan,
                isYearly: yearly,
                subscriptionPrices,
                country,
              })
            );

      void analytics.track(event, {
        label: `${plan}-${yearly ? 'yearly' : 'monthly'}`,
        userId: user?.id,
        plan: user ? user.plan || SubscriptionPlanType.FREE : undefined,
        productName: plan,
        productValue,
        productPeriod: yearly ? 'yearly' : 'monthly',
        ...extra,
      });
    },
    [yearly, subscriptionPrices, country, user]
  );

  const {
    setPlan,
    setMembersQuantity,
    setUpgrade,
    setFromSubscription,
    reset,
  } = usePlanSelectionStore(planSelectionStoreSelector.actions);

  const handlePlanSelected = useCallback(
    (plan: SubscriptionPlanType): void => {
      handleTrackPlan(plan, SUBSCRIPTION_GET_PLAN);

      if (isTeam && !isTeamAdmin) {
        setShowModal(Modals.RequestUpgrade);
        return;
      }

      if (freeTrial.isPaidByKittl) {
        // TODO: this is pretty pointless, we need to figure out what to do with users on free trial
        openBillingPortal();
        return;
      }

      if (isTeam) {
        setUpgrade(true);
      }

      if (
        plan === SubscriptionPlanType.PRO ||
        plan === SubscriptionPlanType.EXPERT
      ) {
        setPlan(plan);
        if (freeTrial.isAvailable) {
          return showUpgradeModal('freeTrial');
        }
      } else if (currentSubscription?.plan) {
        // If the user is downgrading from a paid plan to the free plan, we need to set their current plan
        setFromSubscription(currentSubscription);
      }
      setShowModal(Modals.Subscription);
    },
    [
      handleTrackPlan,
      freeTrial,
      openBillingPortal,
      setPlan,
      setUpgrade,
      isTeam,
      isTeamAdmin,
      currentSubscription,
      setFromSubscription,
      showUpgradeModal,
    ]
  );

  // we use useEffect to make sure we select the subscription after the user and subscription stores are initialized
  useEffect(() => {
    if (user?.confirmed && subscriptionInitialized && subscribeAfterAuth) {
      setSubscribeAfterAuth(null);
      void handlePlanSelected(subscribeAfterAuth);
    }
  }, [user, subscribeAfterAuth, handlePlanSelected, subscriptionInitialized]);

  const handleButtonClick = async (
    plan: SubscriptionPlanType
  ): Promise<void> => {
    if (!user) {
      onAuthenticate?.(plan === SubscriptionPlanType.FREE, () => {
        setSubscribeAfterAuth(plan);
      });
      return;
    }

    await handlePlanSelected(plan);
  };

  const isPlanChange = (plan: SubscriptionPlanType): boolean => {
    // if the plan of the button is different to the current plan of the user
    if (!currentPlan || currentPlan !== plan) {
      return true;
    }

    // if the user has selected a different addon
    if (
      plan === SubscriptionPlanType.EXPERT &&
      expertAddonPriceId !==
        (getAddonFromSubscription(currentSubscription)?.stripeId ?? null)
    ) {
      return true;
    }

    // if the user has selected a different quantity
    const currentSubscriptionPlan = currentSubscription?.plan;
    if (
      currentSubscriptionPlan &&
      // We don't need to check the quantity if the workspace feature is enabled
      !workspaceFeatureEnabled &&
      'quantity' in currentSubscriptionPlan &&
      quantity !== currentSubscriptionPlan.quantity
    ) {
      return true;
    }

    return false;
  };

  const getBusinessPlanButtonProps = (): Pick<
    ButtonProps,
    'label' | 'onClick' | 'disabled' | 'theme'
  > => {
    return {
      onClick: () => {
        window.open(
          getOnClickBusinessLink({ name: user?.name, email: user?.email }),
          '_blank'
        );
      },
      theme: secondaryTheme,
    };
  };

  const getPlanButtonProps = (
    plan: SubscriptionPlanType,
    isRecommendedPlan = true
  ): Pick<ButtonProps, 'label' | 'onClick' | 'disabled' | 'theme'> => {
    const isChange = isPlanChange(plan);
    const isFreeTrialActive = freeTrial.isRunning && !freeTrial.isCancelled;

    let label: string | undefined = undefined;

    if (currentPlan === plan && isChange) {
      label = 'Change';
    } else if (currentPlan === plan && isFreeTrialActive) {
      label = 'Free Trial Activated';
    } else if (currentPlan === plan) {
      label = 'Your Plan';
    }

    const isDowngradingOnTrial =
      isFreeTrialActive &&
      (plan === SubscriptionPlanType.FREE ||
        (currentPlan === SubscriptionPlanType.EXPERT &&
          plan === SubscriptionPlanType.PRO));

    return {
      label,
      onClick: () => {
        void handleTrackPlan(plan, SUBSCRIPTION_CLICK_PLAN, {
          isDowngradingOnTrial,
          isFreeTrialActive,
        });

        const handler = isDowngradingOnTrial
          ? (): void => setShowModal(Modals.Offboarding)
          : (): Promise<void> => handleButtonClick(plan);

        handler();
      },
      disabled: !ENABLE_BILLING || !isChange || !subscriptionInitialized,
      theme:
        !isRecommendedPlan || isDowngradingOnTrial ? secondaryTheme : undefined,
    };
  };

  const handleShowCompare = (): void => {
    if (showCompare) {
      const topDiv = document?.querySelector(
        '#pricingplans-top'
      ) as HTMLElement;
      if (window && topDiv) {
        window.scrollTo(topDiv.offsetLeft, topDiv.offsetTop - 100);
      }
    } else {
      void analytics.track(SUBSCRIPTION_SHOW_COMPARISON_TABLE);
    }
    setShowCompare(!showCompare);
  };

  const memberSelect = isTeam ? (
    <MemberSelect
      selectedNumberOfMembers={quantity}
      onSelect={(id): void => {
        setMembersQuantity(id);
      }}
    />
  ) : null;

  if (!pricingConfig) {
    return null;
  }

  if (!subscriptionInitialized) {
    return null;
  }

  const recommendedLabel = 'RECOMMENDED';

  const getExpertPlanLabel = () => {
    if (currentPlan !== SubscriptionPlanType.EXPERT) {
      return recommendedLabel;
    }
    return undefined;
  };

  const isMobileScreen = (width ?? 0) <= BREAKPOINTS.tabletMin;

  const isSubscriptionModalOpen = Boolean(
    user && showModal === Modals.Subscription
  );

  return (
    <div
      id="pricingplans-top"
      style={
        isMobileScreen
          ? { width: '100%' }
          : { width: '100%', overflowX: 'auto' }
      }
    >
      <PricingTable>
        <PlanContainersWrapper
          ref={containerRef}
          data-testid="pricing-page--pricing-plans"
          plans={businessPlanIsEnabled ? 4 : 3}
        >
          <NewPricingPlan
            {...pricingConfig.FREE}
            button={getPlanButtonProps(SubscriptionPlanType.FREE, false)}
            index={0}
            memberSelect={isTeam ? <FreePlanMemberSelect /> : null}
            quantity={quantity}
            type={SubscriptionPlanType.FREE}
            yearly={yearly}
          />
          <NewPricingPlan
            {...pricingConfig.PRO}
            button={getPlanButtonProps(SubscriptionPlanType.PRO, false)}
            index={1}
            isHighlighted={false}
            memberSelect={memberSelect}
            quantity={quantity}
            type={SubscriptionPlanType.PRO}
            yearly={yearly}
          />
          <NewPricingPlan
            {...pricingConfig.EXPERT}
            button={getPlanButtonProps(
              SubscriptionPlanType.EXPERT,
              getExpertPlanLabel() !== undefined
            )}
            index={2}
            isHighlighted={true}
            label={getExpertPlanLabel()}
            memberSelect={memberSelect}
            quantity={quantity}
            selectedAddon={selectedExpertAddon}
            type={SubscriptionPlanType.EXPERT}
            yearly={yearly}
          />
          {businessPlanIsEnabled && (
            <NewPricingPlan
              {...pricingConfig.BUSINESS}
              button={getBusinessPlanButtonProps()}
              index={3}
              memberSelect={memberSelect}
              quantity={quantity}
              selectedAddon={selectedExpertAddon}
              type={SubscriptionTierType.BUSINESS}
              yearly={yearly}
            />
          )}
        </PlanContainersWrapper>
        <VatNotice>
          * The prices displayed are inclusive of Value Added Tax (VAT).
          Payments through credit and debit cards are acceptable.
        </VatNotice>
        <Spacer height="48px" />
        <ComparisonWrapper>
          {showCompare && (
            <>
              <ComparisonTable
                plans={[
                  {
                    ...pricingConfig.FREE,
                    button: getPlanButtonProps(
                      SubscriptionPlanType.FREE,
                      false
                    ),
                  },
                  {
                    ...pricingConfig.PRO,
                    button: getPlanButtonProps(SubscriptionPlanType.PRO, false),
                  },
                  {
                    ...pricingConfig.EXPERT,
                    button: getPlanButtonProps(
                      SubscriptionPlanType.EXPERT,
                      getExpertPlanLabel() !== undefined
                    ),
                  },
                ]}
                selectedExpertAddon={selectedExpertAddon}
              />
              <Spacer height="38px" />
            </>
          )}
          <Button
            height="40px"
            label={showCompare ? 'Collapse Plans' : 'Compare Plans'}
            name="toggleComparePlansButton"
            theme={secondaryTheme}
            width="260px"
            onClick={handleShowCompare}
          />
        </ComparisonWrapper>
      </PricingTable>
      <DecisionModal
        firstButton={{
          label: 'Request Upgrade',
          type: 'primary',
          width: 'auto',
          onClick: async (): Promise<void> => {
            const response = await teamsApi.requestUpgrade();
            if (!response.success) {
              fireToast({
                label:
                  'Failed to send a request to upgrade your plan. Please try again.',
                error: true,
                duration: 5000,
              });
            } else {
              fireToast({
                label:
                  'Sent a request to upgrade your plan to your team admin.',
                duration: 5000,
              });
              setShowModal(null);
            }
          },
        }}
        isOpen={showModal === Modals.RequestUpgrade}
        message={
          <>
            You don’t have permission to edit team subscriptions. Only your team
            admin can upgrade your account.
          </>
        }
        title="Upgrade Plan"
        width="400px"
        onClose={(): void => setShowModal(null)}
      />
      <SubscriptionModal
        shouldShowOnlyOnePlan
        isOpen={isSubscriptionModalOpen}
        returnUrlParams={{
          [SUCCESS_UPGRADE_PAYMENT_URL_PARAM]:
            SUCCESS_UPGRADE_PAYMENT_URL_PARAM_VALUE,
        }}
        onClose={(): void => {
          reset();
          setShowModal(null);
        }}
      />
      {user && currentSubscription && (
        <SubscriptionOffboardingModal
          isOpen={showModal === Modals.Offboarding}
          subscription={currentSubscription}
          user={user}
          onClose={(): void => setShowModal(null)}
        />
      )}
    </div>
  );
};

export default withEventScope(PricingPlans, { feature: 'pricingPlans' });
